import type { TestTask, TaskBlock, TaskAnswer } from "@/lib/testTypes";

type UnknownItem = {
  number?: unknown;
  question?: unknown;
  text?: unknown;
  options?: unknown;
  answer?: unknown;
};

type UnknownTask = {
  blocks?: unknown;
  title?: unknown;
  label?: unknown;
  instruction?: unknown;
  points?: unknown;
  pointsLabel?: unknown;
  pointsNote?: unknown;
  items?: unknown;
  answers?: unknown;
};

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asItemArray(items: unknown): UnknownItem[] {
  return Array.isArray(items) ? (items as UnknownItem[]) : [];
}

function formatLegacyQuestion(item: UnknownItem): string {
  const base = asString(item.question, asString(item.text));
  const options = Array.isArray(item.options)
    ? item.options.filter((option): option is string => typeof option === "string")
    : [];
  if (options.length === 0) {
    return base;
  }
  return `${base} (${options.join(" / ")})`;
}

function normalizeLegacyAnswers(items: UnknownItem[]): TaskAnswer[] {
  return items
    .filter((item) => typeof item.answer === "string" && item.answer.trim().length > 0)
    .map((item) => ({
      blockIndex: 0,
      itemRef: asNumber(item.number),
      answer: item.answer as string,
    }));
}

export function ensureBlocks(task: UnknownTask): TestTask {
  if (Array.isArray(task.blocks)) {
    return task as TestTask;
  }

  const items = asItemArray(task.items);
  const title = asString(task.title).trim()
    ? asString(task.title)
    : `${asString(task.label)}. ${asString(task.instruction)}`.trim();

  const blocks: TaskBlock[] = [
    {
      type: "numbered_list",
      items: items.map((item) => ({
        number: asNumber(item.number),
        text: formatLegacyQuestion(item),
      })),
    },
  ];

  const answers = normalizeLegacyAnswers(items);

  return {
    title,
    points: asNumber(task.points),
    pointsLabel: asString(task.pointsLabel, asString(task.pointsNote)),
    blocks,
    answers: answers.length > 0 ? answers : undefined,
  };
}
